import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Chip, FormControl, MenuItem, Select, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { CompleteOperationType, StartOperationType } from 'model/alerts.model';
import React, { useEffect, useRef, useState } from 'react';
import { TaskService } from '../../../../service/task.service';
import zeplinColor from '../../../../theme/Color';
import { Constants } from '../../../../utilities/constants';
import { AssigneeAutocomplete } from '../../AssigneeAutocomplete';
import style from '../CustomViewTask.module.css';
import { useTaskFieldUpdates } from '../hooks/useTaskFieldUpdates';
import { TaskFieldRow } from './TaskFieldRow';

interface TaskFieldsProps {
  selectedData: any;
  loading: boolean;
  completeOperation: CompleteOperationType;
  startOperation: StartOperationType;
  onUpdate?: (updatedTaskData?: any) => void;
}

export const TaskFields: React.FC<TaskFieldsProps> = ({
  selectedData,
  loading,
  onUpdate,
  completeOperation,
}) => {
  const [showAssigneeAutocomplete, setShowAssigneeAutocomplete] = useState(false);
  const assigneeTextRef = useRef<HTMLDivElement>(null);
  const [StatusOptions, setStatusOptions] = useState<string[]>(['To Do', 'In Progress', 'Done']);
  const [PriorityOptions, setPriorityOptions] = useState<string[]>(['Medium', 'Low', 'High']);
  const [dropdownsLoading, setDropdownsLoading] = useState(false);

  const { updating, updateTaskField } = useTaskFieldUpdates({
    selectedData,
    completeOperation,
    onUpdate,
  });

  useEffect(() => {
    const fetchDropdowns = async () => {
      setDropdownsLoading(true);
      try {
        const res = await TaskService.getTaskDropdowns();
        if (res?.priorityOrder) {
          setPriorityOptions(Object.keys(res.priorityOrder));
        }
        if (res?.recordStatusTypes) {
          setStatusOptions(Object.keys(res.recordStatusTypes));
        }
      } catch (err) {
      } finally {
        setDropdownsLoading(false);
      }
    };

    fetchDropdowns();
  }, []);

  const getStatusValue = (status: string) => {
    if (status === 'Completed') {
      const completedMatch = StatusOptions.find(
        (opt) => opt.toLowerCase() === 'done' || opt.toLowerCase() === 'completed',
      );
      return completedMatch || StatusOptions[0] || 'To Do';
    }
    if (StatusOptions.includes(status)) return status;
    const match = StatusOptions.find((opt) => opt.toLowerCase() === status.toLowerCase());
    return match || StatusOptions[0] || 'To Do';
  };

  const handleAssigneeChange = async (value: any) => {
    if (Array.isArray(value) && value.length > 0) {
      await updateTaskField('assignee', value);
    } else if (value && value.Id) {
      await updateTaskField('assignee', value);
    } else if (!value || (Array.isArray(value) && value.length === 0)) {
      await updateTaskField('assignee', null);
    }
  };

  const handleStatusChange = async (value: string) => {
    await updateTaskField('status', value);
  };

  const handlePriorityChange = async (value: string) => {
    await updateTaskField('priority', value);
  };

  const getCurrentAssignee = () => {
    let assigneesArray: Array<{
      Id: string;
      Name: string;
      Email?: string;
      profile_image_url?: string;
      avatar?: string;
    }> | null = null;

    if (selectedData?.assignedTo && Array.isArray(selectedData.assignedTo)) {
      assigneesArray = (selectedData.assignedTo as Array<any>)
        .filter(
          (assignee: any) =>
            assignee && typeof assignee === 'object' && assignee?.Id && assignee?.Name,
        )
        .map((assignee: any) => ({
          Id: assignee.Id,
          Name: assignee.Name,
          Email: assignee?.Email,
          profile_image_url: assignee?.profile_image_url,
          avatar: assignee?.avatar || assignee?.profile_image_url,
        }));
    }
    if ((!assigneesArray || assigneesArray.length === 0) && selectedData?.assignedTo) {
      if (Array.isArray(selectedData.assignedTo) && selectedData.assignedTo.length > 0) {
        assigneesArray = selectedData.assignedTo
          .filter((assignee: any) => assignee?.Id && assignee?.Name)
          .map((assignee: any) => ({
            Id: assignee.Id,
            Name: assignee.Name,
            Email: assignee?.Email,
            profile_image_url: assignee?.profile_image_url,
            avatar: assignee?.avatar || assignee?.profile_image_url,
          }));
      } else if (
        typeof selectedData.assignedTo === 'object' &&
        selectedData.assignedTo.Id &&
        selectedData.assignedTo.Name
      ) {
        return {
          Id: selectedData.assignedTo.Id,
          Name: selectedData.assignedTo.Name,
          Email: selectedData.assignedTo.Email,
          profile_image_url:
            selectedData.assignedTo.profile_image_url || selectedData.assignedTo.avatar,
          avatar: selectedData.assignedTo.avatar || selectedData.assignedTo.profile_image_url,
        };
      }
    }
    if (!assigneesArray || assigneesArray.length === 0) return null;
    if (assigneesArray.length === 1) return assigneesArray[0];
    return assigneesArray;
  };

  const currentAssignee = getCurrentAssignee();
  const hasMultipleAssignees = Array.isArray(currentAssignee) && currentAssignee.length > 1;
  const assigneeCount = Array.isArray(currentAssignee)
    ? currentAssignee.length
    : currentAssignee
      ? 1
      : 0;

  useEffect(() => {
    if (!hasMultipleAssignees && showAssigneeAutocomplete) {
      setShowAssigneeAutocomplete(false);
    }
  }, [hasMultipleAssignees, showAssigneeAutocomplete]);

  return (
    <>
      <Box sx={{ display: 'flex', gap: { xs: '20px', xl: '24px' }, flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', gap: '8px', flex: 1, alignItems: 'center' }}>
          <img
            src='/assets/user-multiple.svg'
            className={style['field-icon']}
            style={{ objectFit: 'fill' }}
          />
          <Typography variant='responsiveBody2' color={zeplinColor.Neutral8} sx={{ flex: 1 }}>
            Assignee
          </Typography>
          {loading ? (
            <Box sx={{ flex: 1 }}>Loading...</Box>
          ) : hasMultipleAssignees ? (
            <Box
              ref={assigneeTextRef}
              sx={{
                flex: 1,
                cursor: 'pointer',
                padding: '8px 12px',
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: zeplinColor.Neutral4,
                },
                transition: 'background-color 0.2s',
              }}
              onClick={() => setShowAssigneeAutocomplete(true)}
            >
              <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                {assigneeCount} assignees
              </Typography>
            </Box>
          ) : (
            <Box sx={{ flex: 1, position: 'relative' }}>
              <AssigneeAutocomplete
                value={getCurrentAssignee()}
                onChange={handleAssigneeChange}
                disabled={updating}
                loading={updating}
                multiple={true}
              />
            </Box>
          )}
        </Box>

        <TaskFieldRow
          icon='/assets/type-logo.svg'
          label='Related Type'
          loading={loading}
          value={selectedData?.relatedType ?? 'N/A'}
        />

        <TaskFieldRow
          icon='/assets/account.svg'
          label='Related To'
          loading={loading}
          value={selectedData?.relatedTo?.Id ? selectedData?.relatedTo?.Name : 'N/A'}
        />

        <TaskFieldRow
          icon='/assets/type-logo.svg'
          label='Name Type'
          loading={loading}
          value={
            selectedData?.nameType && selectedData?.nameType !== '' ? selectedData?.nameType : 'N/A'
          }
        />
        <TaskFieldRow
          icon='/assets/user-single.svg'
          label='Name'
          loading={loading}
          iconWidth={20}
          iconHeight={20}
          value={selectedData?.clientName ?? 'N/A'}
        />

        {selectedData?.recordTypeName === 'Payment Task' && (
          <>
            <TaskFieldRow
              icon='/assets/payee.svg'
              label='Payee'
              loading={loading}
              value={selectedData?.payee?.Id ? selectedData?.payee?.Name : 'N/A'}
            />
            <TaskFieldRow
              icon='/assets/qty.svg'
              label='QTY'
              loading={loading}
              value={selectedData?.qty ?? 'N/A'}
            />
            <TaskFieldRow
              icon='/assets/rate.svg'
              label='Rate'
              loading={loading}
              value={selectedData?.rate ?? 'N/A'}
            />
            <TaskFieldRow
              icon='/assets/amount.svg'
              label='Amount'
              loading={loading}
              value={selectedData?.amount ?? 'N/A'}
            />
            <TaskFieldRow
              icon='/assets/terms.svg'
              label='Terms'
              loading={loading}
              value={selectedData?.terms ?? 'N/A'}
            />
            <TaskFieldRow
              icon='/assets/email.svg'
              label='Email'
              loading={loading}
              value={selectedData?.email ?? 'N/A'}
            />
          </>
        )}

        <Box sx={{ display: 'flex', gap: '8px', flex: 1, alignItems: 'center' }}>
          <img
            src='/assets/status.svg'
            className={style['field-icon']}
            style={{ objectFit: 'fill' }}
          />
          <Typography variant='responsiveBody2' color={zeplinColor.Neutral8} sx={{ flex: 1 }}>
            Status
          </Typography>
          {loading || dropdownsLoading ? (
            <Box sx={{ flex: 1 }}>Loading...</Box>
          ) : (
            <FormControl sx={{ flex: 1 }}>
              <Select
                value={getStatusValue(selectedData?.status || StatusOptions[0] || 'To Do')}
                onChange={(e) => handleStatusChange(e.target.value as string)}
                disabled={updating}
                IconComponent={KeyboardArrowDownIcon}
                sx={{
                  height: '29px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '& .MuiSelect-select': {
                    padding: '4px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    paddingRight: '32px',
                  },
                  '& .MuiSelect-icon': {
                    right: '8px',
                    color: zeplinColor.BasicBlack,
                  },
                }}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Chip
                      label={<Typography variant='responsiveLabel1'>{selected}</Typography>}
                      sx={{
                        backgroundColor:
                          Constants.StatusTask[selected as keyof typeof Constants.StatusTask] ||
                          zeplinColor.BasicBlack,
                        color: zeplinColor.Neutral1,
                        '&.MuiChip-root': {
                          height: '29px',
                          padding: '4px 14px',
                          borderRadius: '50px',
                        },
                        '& .MuiChip-label': {
                          padding: 0,
                        },
                      }}
                    />
                  </Box>
                )}
              >
                {StatusOptions.map((status) => (
                  <MenuItem key={status} value={status}>
                    {status}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        <Box sx={{ display: 'flex', gap: '8px', flex: 1, alignItems: 'center' }}>
          <img
            src='/assets/priority.svg'
            className={style['field-icon']}
            style={{ objectFit: 'fill' }}
          />
          <Typography variant='responsiveBody2' color={zeplinColor.Neutral8} sx={{ flex: 1 }}>
            Priority
          </Typography>
          {loading || dropdownsLoading ? (
            <Box sx={{ flex: 1 }}>Loading...</Box>
          ) : (
            <FormControl sx={{ flex: 1 }}>
              <Select
                value={selectedData?.priority || PriorityOptions[0] || 'Medium'}
                onChange={(e) => handlePriorityChange(e.target.value as string)}
                disabled={updating}
                IconComponent={KeyboardArrowDownIcon}
                sx={{
                  height: '29px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '& .MuiSelect-select': {
                    padding: '4px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    paddingRight: '32px',
                  },
                  '& .MuiSelect-icon': {
                    right: '8px',
                    color: zeplinColor.BasicBlack,
                  },
                }}
                renderValue={(selected) => {
                  const priority = selected || PriorityOptions[0] || 'Medium';
                  const colorKey = priority === 'Medium' ? 'Normal' : priority;
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Chip
                        label={<Typography variant='responsiveLabel1'>{selected}</Typography>}
                        sx={{
                          bgcolor:
                            Constants.PriorityTask[colorKey as 'Normal' | 'Low' | 'High'] ||
                            Constants.PriorityTask.Normal,
                          color: zeplinColor.Neutral1,
                          '&.MuiChip-root': {
                            height: '29px',
                            padding: '4px 14px',
                            borderRadius: '50px',
                          },
                          '& .MuiChip-label': {
                            padding: 0,
                          },
                        }}
                      />
                    </Box>
                  );
                }}
              >
                {PriorityOptions.map((priority) => (
                  <MenuItem key={priority} value={priority}>
                    {priority}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        <TaskFieldRow
          icon='/assets/calendar-fade.svg'
          label='Due Date'
          loading={loading}
          value={selectedData?.dueDate ? dayjs(selectedData.dueDate).format('DD MMM, YYYY') : 'NA'}
        />

        <TaskFieldRow
          icon='/assets/user-single.svg'
          label='Created By'
          loading={loading}
          iconWidth={20}
          iconHeight={20}
          value={
            typeof selectedData?.createdBy === 'string'
              ? selectedData.createdBy
              : selectedData?.createdBy?.Name || selectedData?.createdByName || 'N/A'
          }
        />

        <TaskFieldRow
          icon='/assets/calendar-fade.svg'
          label='Created Date'
          loading={loading}
          value={
            selectedData?.createdDate || selectedData?.createdAt
              ? dayjs(selectedData.createdDate || selectedData.createdAt).format('DD MMM, YYYY')
              : 'NA'
          }
        />

        <TaskFieldRow
          icon='/assets/calendar-fade.svg'
          label='Last Modified Date'
          loading={loading}
          value={
            selectedData?.updatedAt || selectedData?.lastModifiedDate || selectedData?.modifiedDate
              ? dayjs(
                  selectedData.updatedAt ||
                    selectedData.lastModifiedDate ||
                    selectedData.modifiedDate,
                ).format('DD MMM, YYYY')
              : 'NA'
          }
        />
      </Box>
      {hasMultipleAssignees && showAssigneeAutocomplete && (
        <AssigneeAutocomplete
          value={getCurrentAssignee()}
          onChange={async (value) => {
            await handleAssigneeChange(value);
          }}
          disabled={updating}
          loading={updating}
          multiple={true}
          open={showAssigneeAutocomplete}
          onClose={() => {
            setShowAssigneeAutocomplete(false);
          }}
          hideField={true}
          anchorEl={assigneeTextRef.current}
        />
      )}
    </>
  );
};
